import React from 'react';
import { useTranslation } from 'react-i18next';
import { createTelegramChannelAccount } from '../services/channelAccountService';
import styles from './ConnectorsPage.module.css';

interface AddTelegramAccountFormProps {
  canManage: boolean;
  onCreated: () => void;
  onCancel: () => void;
}

const emptyForm = {
  displayName: '',
  botToken: '',
  webhookSecretToken: '',
  apiBaseUrl: 'https://api.telegram.org',
  isDefault: false,
};

export const AddTelegramAccountForm: React.FC<AddTelegramAccountFormProps> = ({
  canManage,
  onCreated,
  onCancel,
}) => {
  const { t } = useTranslation(['companies', 'admin']);
  const [form, setForm] = React.useState(emptyForm);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const save = async () => {
    if (!form.displayName.trim() || !form.botToken.trim()) {
      setError(t('channelAccountAddTelegramRequiredFields', { ns: 'companies' }));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await createTelegramChannelAccount({
        displayName: form.displayName.trim(),
        botToken: form.botToken.trim(),
        webhookSecretToken: form.webhookSecretToken.trim() || undefined,
        apiBaseUrl: form.apiBaseUrl.trim() || undefined,
        isDefault: form.isDefault,
      });
      setForm(emptyForm);
      onCreated();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('failedToCreateChannelAccount', { ns: 'companies' }));
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    return null;
  }

  return (
    <div className={styles.channelAccountAddForm}>
      <h4>{t('channelAccountAddTelegramHeading', { ns: 'companies' })}</h4>
      <p style={{ color: '#516173', marginTop: 0 }}>
        {t('channelAccountAddTelegramHelp', { ns: 'companies' })}
      </p>

      {error ? <div className="error">{error}</div> : null}

      <div className="company-form-grid">
        <label>
          {t('channelAccountDisplayName', { ns: 'companies' })}
          <input
            className="text-input"
            value={form.displayName}
            onChange={event => setForm(prev => ({ ...prev, displayName: event.target.value }))}
            placeholder={t('channelAccountDisplayNamePlaceholder', { ns: 'companies' })}
          />
        </label>

        <label>
          {t('telegramBotToken', { ns: 'companies' })}
          <input
            className="text-input"
            type="password"
            value={form.botToken}
            onChange={event => setForm(prev => ({ ...prev, botToken: event.target.value }))}
            autoComplete="new-password"
          />
        </label>

        <label className={styles.channelAccountCheckbox}>
          <input
            type="checkbox"
            checked={form.isDefault}
            onChange={event => setForm(prev => ({ ...prev, isDefault: event.target.checked }))}
          />
          {t('channelAccountDefaultLabel', { ns: 'companies' })}
        </label>
      </div>

      <div className="company-actions-inline" style={{ marginTop: 12 }}>
        <button type="button" className="btn btn-primary" onClick={() => { void save(); }} disabled={saving}>
          {saving ? t('savingEllipsis', { ns: 'admin' }) : t('channelAccountCreate', { ns: 'companies' })}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
          {t('channelAccountCancelAdd', { ns: 'companies' })}
        </button>
      </div>
    </div>
  );
};
